import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { clientsAPI, documentsAPI } from '../services/api';

const RecuCreationModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [documentDate, setDocumentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([
    { reference: '', description: '', quantity: 1, unit_price_ht: 0, vat_rate: 19 }
  ]);
  const [globalVatRate, setGlobalVatRate] = useState(19);

  useEffect(() => {
    if (open) {
      loadClients();
      resetForm();
    }
  }, [open]);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const resetForm = () => {
    setSelectedClient('');
    setDocumentNumber('');
    setDocumentDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setItems([
      { reference: '', description: '', quantity: 1, unit_price_ht: 0, vat_rate: 19 }
    ]);
    setGlobalVatRate(19);
  };

  const addItem = () => {
    setItems([...items, { reference: '', description: '', quantity: 1, unit_price_ht: 0, vat_rate: 19 }]);
  };

  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const calculateTotals = () => {
    const totalHT = items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unit_price_ht) || 0;
      return sum + (quantity * unitPrice);
    }, 0);

    const totalVAT = totalHT * (globalVatRate / 100);
    const totalTTC = totalHT + totalVAT;

    return { totalHT, totalVAT, totalTTC };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClient) {
      alert('Veuillez sélectionner un client');
      return;
    }

    if (items.every(item => !item.description.trim())) {
      alert('Veuillez ajouter au moins une ligne de description');
      return;
    }

    setLoading(true);
    try {
      const { totalHT, totalVAT, totalTTC } = calculateTotals();
      
      const recuData = {
        client: selectedClient,
        document_date: documentDate,
        notes: notes,
        invoice_details: {
          period_start: documentDate,
          period_end: documentDate,
          global_vat_rate: globalVatRate,
          total_ht: totalHT,
          total_vat: totalVAT,
          total_ttc: totalTTC,
        },
        invoice_items: items.map((item, index) => ({
          line_number: index + 1,
          reference: item.reference,
          description: item.description,
          line_type: 'item',
          quantity: item.quantity,
          months: 1,
          unit_price_ht: item.unit_price_ht,
          vat_rate: item.vat_rate,
        }))
      };

      // Create the document with type 'recu'
      const response = await documentsAPI.create({
        ...recuData,
        document_type: 'recu'
      });

      if (onSuccess) {
        onSuccess(response.data);
      }
      onClose();
    } catch (err) {
      console.error('Error creating reçu:', err);
      alert(err.response?.data?.message || 'Erreur lors de la création du Reçu');
    } finally {
      setLoading(false);
    }
  };

  const { totalHT, totalVAT, totalTTC } = calculateTotals();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptIcon />
          <Typography variant="h6">Créer un Reçu</Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Client</InputLabel>
                <Select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  label="Client"
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.company_name} - {client.contact_person}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Date du Reçu"
                value={documentDate}
                onChange={(e) => setDocumentDate(e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes (optionnel)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom>Articles du Reçu</Typography>
          
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Référence</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Quantité</TableCell>
                  <TableCell>Prix Unitaire HT</TableCell>
                  <TableCell>TVA (%)</TableCell>
                  <TableCell>Total HT</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        size="small"
                        value={item.reference}
                        onChange={(e) => updateItem(index, 'reference', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.unit_price_ht}
                        onChange={(e) => updateItem(index, 'unit_price_ht', e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.vat_rate}
                        onChange={(e) => updateItem(index, 'vat_rate', e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${((item.quantity || 0) * (item.unit_price_ht || 0)).toFixed(2)} DZD`}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {items.length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeItem(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            startIcon={<AddIcon />}
            onClick={addItem}
            sx={{ mb: 2 }}
          >
            Ajouter une ligne
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>TVA Globale</InputLabel>
              <Select
                value={globalVatRate}
                onChange={(e) => setGlobalVatRate(e.target.value)}
                label="TVA Globale"
              >
                <MenuItem value={0}>0%</MenuItem>
                <MenuItem value={9}>9%</MenuItem>
                <MenuItem value={19}>19%</MenuItem>
              </Select>
            </FormControl>
            
            <Box textAlign="right">
              <Typography variant="body2">Total HT: {totalHT.toFixed(2)} DZD</Typography>
              <Typography variant="body2">TVA ({globalVatRate}%): {totalVAT.toFixed(2)} DZD</Typography>
              <Typography variant="h6">Total TTC: {totalTTC.toFixed(2)} DZD</Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Création...' : 'Créer le Reçu'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RecuCreationModal;
